import React, { useState, useContext, useEffect, useRef } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";

import SuiButton from "components/SuiButton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import SuiSelect from "components/SuiSelect";
import Pagination from "@mui/material/Pagination";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiBadge from "components/SuiBadge";
import SuiInput from "components/SuiInput";

import { StoreContext } from "context/store";
import { AccountContext } from "context/account";

import api from "services/api";

import qs from "query-string";
import toast from "react-hot-toast";

import PostCardOwner from "pages/de/posts/components/PostCardOwner";

import Tooltip from "@mui/material/Tooltip";
import { useForm, Controller } from "react-hook-form";
import PublishIcon from "@mui/icons-material/Publish";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import colors from "assets/theme/base/colors";
import PostDetailModal from "pages/de/posts/detail";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

const PostManagementListPage = () => {
  const [workTab, setWorkTab] = useState("published");
  const [postList, setPostList] = useState({ published: [], draft: [] });
  const [filteredPostList, setFilteredPostList] = useState({ published: [], draft: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState({});

  const cardRef = useRef(null);

  let location = useLocation();
  const navigate = useNavigate();
  const url = location.href ? location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url);
  const status = urlObj?.query?.status;

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    retrievePosts(0, pageSize);
  }, []);

  useEffect(() => {
    if (status) {
      setWorkTab(status);
    }
  }, [status, postList]);

  const retrievePosts = async (offset, limit) => {
    try {
      const { data } = await api.post.list({ order: "-updated_at" });
      const allPosts = data?.posts || [];
      const publishedList = allPosts.filter((x) => x.status == "published");
      const draftList = allPosts.filter((x) => x.status == "draft");

      setPostList({ published: publishedList, draft: draftList });
      setFilteredPostList({
        published: publishedList.slice(0, pageSize),
        draft: draftList.slice(0, pageSize),
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const offset = pageSize * (newPage - 1);
    const limit = pageSize;
    console.log(postList.published.slice(offset, limit));

    setFilteredPostList({
      published: postList.published.slice(offset, offset + limit),
      draft: postList.draft.slice(offset, offset + limit),
    });
  };

  const handleChangeRowsPerPage = (option) => {
    const newPageSize = parseInt(option.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    const offset = 0;
    const limit = newPageSize;
    setFilteredPostList({
      published: postList.published.slice(offset, offset + limit),
      draft: postList.draft.slice(offset, offset + limit),
    });
  };

  const pageCount = parseInt(Math.ceil(postList[workTab].length / pageSize), 10);
  const pageOptions = [];
  for (let i = 0; i < pageCount; i++) {
    pageOptions.push(i);
  }
  const entriesStart = pageSize * (page - 1) + 1;
  const entriesEnd =
    pageSize * page > postList[workTab].length ? postList[workTab].length : pageSize * page;

  return (
    <SuiBox>
      <SuiBox display="flex" justifyContent="space-between" gap={1} alignItems="center">
        <SuiBox dixplay="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <SuiButton
            sx={{ textTransform: "capitalize", p: "0px 8px", mx: 1, minHeight: 24 }}
            color="dark"
            variant={workTab == "published" ? "contained" : "text"}
            fontWeight="bold"
            onClick={() => {
              navigate("/my-posts?status=published");
            }}
          >
            <PublishIcon sx={{ mr: 0.5 }} />
            Published({postList?.["published"]?.length || 0})
          </SuiButton>
          |
          <SuiButton
            sx={{ textTransform: "capitalize", p: "0px 8px", mx: 1, minHeight: 24 }}
            color="dark"
            variant={workTab == "draft" ? "contained" : "text"}
            fontWeight="bold"
            onClick={() => {
              navigate("/my-posts?status=draft");
            }}
          >
            <EditIcon sx={{ mr: 0.5 }} />
            Draft({postList?.["draft"]?.length || 0})
          </SuiButton>
        </SuiBox>
        <SuiButton
          variant={"gradient"}
          size="small"
          color="dark"
          component={Link}
          to="/post/create"
        >
          Create
        </SuiButton>
      </SuiBox>
      <SuiBox sx={{ width: "100%", minHeight: "70vh", mt: 2 }}>
        {filteredPostList?.[workTab] ? (
          <ResponsiveMasonry
            columnsCountBreakPoints={{ 200: 1, 400: 2, 700: 3, 1000: 4, 1200: 5, 1400: 5 }}
          >
            <Masonry gutter="20px">
              {filteredPostList[workTab].map((p, index) => (
                <div
                  style={{
                    overflow: "hidden",
                  }}
                  ref={cardRef}
                >
                  <PostCardOwner
                    index={index}
                    post={p}
                    imageOnly={true}
                    editable
                    sx={{ height: "auto", width: "100%" }}
                    handleRefresh={retrievePosts}
                    handleClick={() => {
                      setPreviewVisible(true);
                      setSelectedPost(p);
                    }}
                  />
                </div>
              ))}
            </Masonry>
            {filteredPostList[workTab].length == 0 ? (
              <Masonry gutter="20px">
                <div style={{ height: "100%" }}>
                  <SuiBox
                    component={Link}
                    to="/post/create"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `dashed 1px ${colors?.lightBlue.main}`,
                      height: "100%",
                      minHeight: 180,
                      width: "100%",
                    }}
                  >
                    <AddIcon fontSize="medium" color="dark" />
                    <SuiTypography variant="h6" color="dark">
                      Create a Post
                    </SuiTypography>
                  </SuiBox>
                </div>
              </Masonry>
            ) : (
              <Masonry gutter="20px"> </Masonry>
            )}
          </ResponsiveMasonry>
        ) : null}
      </SuiBox>
      <SuiBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={3}
      >
        <div className="flex items-center">
          <SuiBox mb={{ xs: 3, sm: 0 }}>
            <SuiTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {postList.estimatedTotalHits} entries
            </SuiTypography>
          </SuiBox>

          <SuiBox display="flex" alignItems="center" gap={1}>
            <SuiSelect
              defaultValue={{ value: pageSize, label: pageSize }}
              options={[10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
              onChange={handleChangeRowsPerPage}
              size="small"
            />
            <SuiTypography variant="caption" color="secondary">
              entries per page
            </SuiTypography>
          </SuiBox>
        </div>
        <Pagination
          variant={"outlined"}
          color={"info"}
          count={pageOptions.length}
          defaultPage={page}
          onChange={handleChangePage}
        />
      </SuiBox>
      {previewVisible && selectedPost ? (
        <PostDetailModal
          handleClose={() => setPreviewVisible(false)}
          visible={previewVisible}
          post={selectedPost}
          setPost={null}
        />
      ) : null}
    </SuiBox>
  );
};

export default PostManagementListPage;
